'use client';

/** @jsxRuntime classic /
/* @jsx jsx */

import { jsx } from '@emotion/react';
import { PrimaryButton } from '@egalite/ui/Buttons/GeneralButtons';
import ErrorList from '@egalite/ui/ErrorList';
import { Check, InputField } from '@egalite/ui/Form';
import InputFieldGroup from '@egalite/ui/Form/InputFieldGroup';
import SelectField from '@egalite/ui/Form/SelectField';
import FormSuccessMessage from '@egalite/ui/IncluiPCD/FormSuccessMessage';
import Typography from '@egalite/ui/Typography';
import { Suspense, useCallback, useState } from 'react';
import useForm from '../../lib/hooks/useForm';
import validationSchema, { initialValues } from '../../lib/validation/companyRegister';
import UTMFields from '../UTMFields';
import Link from 'next/link';

const mutation = async values =>
	fetch('/api/v1/company/register', {
		method: 'POST',
		body: JSON.stringify(values),
		//@ts-ignore-next-line
		duplex: 'half',
		mode: 'cors',
		cache: 'no-cache',
		credentials: 'same-origin',
		redirect: 'follow',
		headers: {
			'Content-Type': 'application/json',
		},
	})
		.then(res => res.json())
		.then(data => data);

const Form = () => {
	const [successRes, setSuccessRes] = useState(false);
	const [createdAsActive, setCreatedAsActive] = useState(false);

	const [formController, getFieldProps, { loading, submitting, success, errors, data, setErrors }] = useForm({
		initialValues,
		validationSchema,
		mutation,
		onSuccess: useCallback(
			data => {
				setSuccessRes(true);
				setCreatedAsActive(data?.status === 'Ativo');
			},
			[setSuccessRes, setCreatedAsActive],
		),
	});

	if (successRes) {
		return (
			<FormSuccessMessage scrollIntoView={true}>
				<>
					<Typography variant="headline-3">Cadastro realizado com sucesso!</Typography>
					{createdAsActive ? (
						<Typography variant="body-1">
							Você recebeu acesso a plataforma da IncluiPcD. Para o seu primeiro acesso será necessário criar uma
							senha que você deverá seguir os seguintes passos:
							<br />
							<br />
							1. Acesse o link <Link href="https://incluipcd.com.br/login/recover" className='text-brand-main underline'>https://incluipcd.com.br/login/recover</Link> e digite seu email;
							<br />
							<br />
							2. Você receberá em até 5 minutos um código de acesso. Lembre de verificar se esse e-mail não foi para a
							caixa de spam. Se mesmo assim você ainda não recebeu o código, entre em contato conosco através desse
							formulário;
							<br />
							<br />
							3. Cadastre a sua senha.
						</Typography>
					) : (
						<Typography variant="body-1">
							Você receberá um email confirmando sua inscrição e em breve a equipe da IncluiPcD entrará em contato
							para ativar seu cadastro!
						</Typography>
					)}
				</>
			</FormSuccessMessage>
		);
	}

	return (
		<form onSubmit={formController.handleSubmit}>
			<fieldset disabled={loading as boolean}>
				<UTMFields values={formController.values} />
				<InputField id="fullnameCompany" type="text" label={'Nome empresa'} {...getFieldProps('fullname')} />
				<InputField id="contactname" type="text" label={'Nome completo do contato'} {...getFieldProps('contactname')} />
				<InputField id="position" type="text" label={'Cargo'} {...getFieldProps('position')} />
				<InputField
					id="cnpj"
					type="text"
					label={'CNPJ'}
					maskOptions={{ mask: '00.000.000/0000-00', lazy: false, placeholderChar: '_' }}
					{...getFieldProps('cnpj')}
				/>
				<InputField id="email" type="email" label={'Email'} {...getFieldProps('email')} />
				<InputFieldGroup
					id="phone"
					type="text"
					label={'Telefone'}
					maskOptions={{
						mask: [
							{ mask: '(00) 0000-0000', lazy: false, placeholderChar: '_' },
							{ mask: '(00) 00000-0000', lazy: false, placeholderChar: '_' },
						],
					}}
					{...getFieldProps('phone')}
				/>
				<SelectField
					id="numEmployees"
					label={'Quantidade de funcionários'}
					{...getFieldProps('numEmployees')}
					options={[
						{ value: '0-99', label: '0 a 99 funcionários' },
						{ value: '100-200', label: '100 a 200 funcionários' },
						{ value: '201-500', label: '201 a 500 funcionários' },
						{ value: '501-1000', label: '501 a 1.000 funcionários' },
						{ value: '1001-5000', label: '1001 a 5.000 funcionários' },
						{ value: '5001-10000', label: '5.001 a 10.000 funcionários' },
						{ value: '10000+', label: '+ de 10.000 funcionários' },
					]}
					className="mb-12"
				/>
				<SelectField
					id="plan"
					label={'Modalidade do plano'}
					{...getFieldProps('plan')}
					options={[
						{ value: 'Plataforma', label: 'Plataforma (recorrente)' },
						{ value: 'Patrocinador', label: 'Patrocinador' },
						{ value: 'Apoiador', label: 'Apoiador' },
						{ value: 'Participante', label: 'Plano gratuito de participante' },
					]}
				/>

				<Check
					id="opt1"
					labelProps={{ css: { alignSelf: 'center' } }}
					{...getFieldProps({ name: 'privacy', type: 'checkbox' })}
				>
					<Typography variant="body-2" asEl="span" className="font-bold text-base-grey-5">
						Declaro que li e concordo com a{' '}
						<a
							href="/privacidade-empresa"
							className="underline"
							target="_blank"
							rel="noopener noreferrer"
						>
							política de privacidade
						</a>
					</Typography>
					<Typography variant="body-2" asEl="span" className="font-bold text-base-grey-5">
						<span className='mx-2 text-black '>e os</span>
						<a
							href="/termos-empresa"
							className="underline"
							target="_blank"
							rel="noopener noreferrer"
						>
							termos de uso
						</a>
					</Typography>
				</Check>

				<ErrorList errors={formController.errors} touched={formController.touched} />

				{errors != null &&
					(Array.isArray(errors) ? errors : [errors]).map((err, index) => (
						<Typography key={`err-${index}`} variant="body-2" className="text-error-dark">
							{err}
						</Typography>
					))}

				<PrimaryButton type="submit" variant="medium" disabled={submitting} xStyle={theme => ({ marginTop: 20 })}>
					Cadastrar
				</PrimaryButton>
			</fieldset>
		</form>
	);
};

const SuspendedForm = () => {
	return (
		<Suspense fallback={<div>Carregando...</div>}>
			<Form />
		</Suspense>
	);
};

export default SuspendedForm;
